/* eslint-disable brace-style */
import { Component, Input, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { FeatureFlagService } from '@core/services/feature-flag/feature-flag.service';
import { PersonaQuestionAndAnswersForComponent, PersonaService } from '@core/services/persona.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { PersonaTypings } from '@core/typings/persona.typing';
import { PanelTypes } from '@yourcause/common/panel';
import { I18nService } from '@yourcause/common/i18n';

///
/// Component used to house Persona Questions and Answers.
/// Shown on the MyAccount Page, MyNonprofits Items, within the flow for Nonprofit Administrator Requests
///
@Component({
    selector: 'yc-persona-question-and-answers',
    templateUrl: './question-and-answers.component.html',
    styleUrls: ['./question-and-answers.component.scss'],
    standalone: false
})

export class PersonaQuestionAndAnswersComponent implements OnInit, OnDestroy {
  //  Determine the source of the user's answers; at which level-- User, Nonprofit, Staged Nonprofit
  @Input() personaQuestionAndAnswerType: PersonaTypings.PersonaQuestionAndAnswerTypes;

  //  Potential Nonprofit of user's answers
  @Input() nonprofitId?: number;
  //  Potential Nonprofit of user's answers
  @Input() nonprofitName?: string;

  //  Potential Staged Nonprofit of user's answers
  @Input() stagedNonprofitId?: number;

  //  If being viewed
  @Input() viewOnly: boolean = false;

  //  If being reviewed
  @Input() beingReviewed: boolean = false;
  //  User belonging to the nonprofit admin request being reviewed
  @Input() userId?: number;
  //  Nonprofit admin request being reviewed; below seem redundant, but we do not have Id for new NARs being created
  @Input() nonprofitAdminRequestId?: number;
  @Input() adminRequest?: boolean = false;

  //  Whether only unanswered questions should be shown
  @Input() unansweredQuestionsOnly: boolean = false;

  //  Whether only unanswered questions should be shown
  @Input() newUserForUnansweredQuestions: boolean = false;

  //  used to disable button for unanswered
  @Output() addEditModalButtonDisabled = new EventEmitter<boolean>();

  //  form for the persona questions and answers
  personaForm: UntypedFormGroup;

  //  feature flag
  personaQuestionsAnswersEnabled = this.featureFlagService.systemQuestionsFlagValue;

  //  data used to build the component when in use in various places throughout BBVN
  personaQuestionsAndAnswersData: PersonaQuestionAndAnswersForComponent[];

  //  subscription for data used to build the component
  sub = new Subscription();

  //  Map of Subjects for debouncing answer updates per question to prevent duplicate API calls
  //  Each question gets its own Subject so updates to different questions don't interfere
  private answerUpdateSubjects = new Map<number, Subject<{
    response: number | number[];
    personaQuestionsAndAnswers: PersonaTypings.PersonaQuestionAndAnswers;
    personaUserAnswers: PersonaTypings.PersonaUserAnswers
      | PersonaTypings.PersonaUserNonprofitAnswers
      | PersonaTypings.PersonaUserStagedNonprofitAnswers
      | PersonaTypings.PersonaUserNonprofitAnswersForReview
      | PersonaTypings.PersonaUserStagedNonprofitAnswersForReview;
  }>>();

  //  Track pending operations to prevent concurrent calls for the same question
  private pendingUpdates = new Map<number, boolean>();

  panelType = PanelTypes.FormSectionHeader;
  isFirst = true;
  title: string;
  description: string;
  descriptionForComponentInModalUse: string = null;

  constructor (
    public formBuilder: UntypedFormBuilder,
    private featureFlagService: FeatureFlagService,
    private personaService: PersonaService,
    private i18nService: I18nService
  ) {}

  ngOnInit () {
    this.buildTitleAndDescription();

    //  build a form with no controls; controls will be added dynamically based on use case.
    this.personaForm = this.formBuilder.group({});

    //  subscribe to the data used to populated the component
    this.sub.add(
      this.personaService.changesTo$('personaQuestionsAndAnswersData')
        .subscribe((data) => {
          this.personaQuestionsAndAnswersData = data;

          //  adds the controls to the form
          this.buildPersonaQuestionsAndAnswers();

          //  persona questions and answers are view only at the moment when looking at an admin request
          //  this includes a user viewing or reviewing
          if (this.viewOnly) {
            this.personaForm.disable();
          }

          if (this.unansweredQuestionsOnly) {
            this.emitAddEditModalButtonDisabledEvent();
          }
        })
    )

    //  set the data for the service/component's use
    const personaServiceDetails: PersonaTypings.PersonaServiceDetails = {
      nonprofitId: this.nonprofitId,
      stagedNonprofitId: this.stagedNonprofitId,
      userId: this.userId,
      nonprofitAdminRequestId: this.nonprofitAdminRequestId,
      beingReviewed: this.beingReviewed,
      unansweredQuestionsOnly: this.unansweredQuestionsOnly
    };

    this.personaService.setPersonaAnswers(personaServiceDetails);
  }

  //  emits if there are remaining questions unanswered
  emitAddEditModalButtonDisabledEvent () {
    if (this.unansweredQuestionsOnly) {
      if (this.personaForm?.controls && Object.keys(this.personaForm.controls).length > 0) {
        let allQuestionsAnswered = true;

        Object.keys(this.personaForm.controls).forEach(formFieldName => {
          const questionAndAnswersControl = this.personaForm.get(formFieldName);

          let questionAnswered = false;

          if (questionAndAnswersControl) {
            const formFieldValue = questionAndAnswersControl.value;

            if (Array.isArray(formFieldValue) && formFieldValue.length > 0) {
              questionAnswered = true;
            } else if (typeof formFieldValue === 'number' && formFieldValue > 0) {
              questionAnswered = true;
            }
          }

          allQuestionsAnswered = allQuestionsAnswered && questionAnswered
        });

        this.addEditModalButtonDisabled.emit(!allQuestionsAnswered);
      } else {
        this.addEditModalButtonDisabled.emit(false);
      }
    } else {
      this.addEditModalButtonDisabled.emit(false);
    }
  }

  //  Handles when a custom response edited
  async editCustomResponsePersonaAnswer (
    systemAnswerId: number,
    userSystemAnswerId: number,
    currentCustomResponse: string,
    personaUserAnswers: PersonaTypings.PersonaUserAnswers
      | PersonaTypings.PersonaUserNonprofitAnswers
      | PersonaTypings.PersonaUserStagedNonprofitAnswers
      | PersonaTypings.PersonaUserNonprofitAnswersForReview
      | PersonaTypings.PersonaUserStagedNonprofitAnswersForReview
  ) {
    const personaQuestionAndAnswerTypeWithDetails: PersonaTypings.PersonaQuestionAndAnswerTypeWithDetails = {
      personaQuestionAndAnswerType: this.personaQuestionAndAnswerType,
      nonprofitId: this.nonprofitId,
      stagedNonprofitId: this.stagedNonprofitId,
      userId: this.userId,
      nonprofitAdminRequestId: this.nonprofitAdminRequestId,
      beingReviewed: this.beingReviewed
    };

    await this.personaService.editCustomResponsePersonaUserAnswer(
      personaUserAnswers,
      systemAnswerId,
      userSystemAnswerId,
      personaQuestionAndAnswerTypeWithDetails,
      currentCustomResponse
    );
  }

  //  Handles when an answer is changed
  onAnswerUpdate (
    response: number|number[],
    personaQuestionsAndAnswers: PersonaTypings.PersonaQuestionAndAnswers,
    personaUserAnswers: PersonaTypings.PersonaUserAnswers
      | PersonaTypings.PersonaUserNonprofitAnswers
      | PersonaTypings.PersonaUserStagedNonprofitAnswers
      | PersonaTypings.PersonaUserNonprofitAnswersForReview
      | PersonaTypings.PersonaUserStagedNonprofitAnswersForReview
    ) {
    if (!this.viewOnly && response) {
      const questionId = personaQuestionsAndAnswers.systemQuestionId;

      // Get or create a Subject for this specific question
      if (!this.answerUpdateSubjects.has(questionId)) {
        const subject = new Subject<{
          response: number | number[];
          personaQuestionsAndAnswers: PersonaTypings.PersonaQuestionAndAnswers;
          personaUserAnswers: PersonaTypings.PersonaUserAnswers
            | PersonaTypings.PersonaUserNonprofitAnswers
            | PersonaTypings.PersonaUserStagedNonprofitAnswers
            | PersonaTypings.PersonaUserNonprofitAnswersForReview
            | PersonaTypings.PersonaUserStagedNonprofitAnswersForReview;
        }>();

        // Set up debounced subscription for this specific question
        this.sub.add(
          subject
            .pipe(
              debounceTime(300), // 300ms debounce to batch rapid changes
              distinctUntilChanged((prev, curr) => {
                // Skip if the response value hasn't actually changed
                return JSON.stringify(prev.response) === JSON.stringify(curr.response);
              })
            )
            .subscribe(async ({ response: debouncedResponse, personaQuestionsAndAnswers: debouncedPersonaQuestionsAndAnswers, personaUserAnswers: debouncedPersonaUserAnswers }) => {
              // Check if an update is already in progress for this question
              if (this.pendingUpdates.get(questionId)) {
                return;
              }

              this.pendingUpdates.set(questionId, true);

              try {
                const personaQuestionAndAnswerTypeWithDetails: PersonaTypings.PersonaQuestionAndAnswerTypeWithDetails = {
                  personaQuestionAndAnswerType: this.personaQuestionAndAnswerType,
                  nonprofitId: this.nonprofitId,
                  stagedNonprofitId: this.stagedNonprofitId,
                  userId: this.userId,
                  nonprofitAdminRequestId: this.nonprofitAdminRequestId,
                  beingReviewed: this.beingReviewed
                };

                await this.personaService.onPersonaAnswerUpdate(
                  debouncedResponse,
                  debouncedPersonaQuestionsAndAnswers,
                  debouncedPersonaUserAnswers,
                  personaQuestionAndAnswerTypeWithDetails,
                  this.unansweredQuestionsOnly
                );

                if (this.unansweredQuestionsOnly) {
                  this.emitAddEditModalButtonDisabledEvent();
                }
              } finally {
                this.pendingUpdates.delete(questionId);
              }
            })
        );

        this.answerUpdateSubjects.set(questionId, subject);
      }

      // Push the update to this question's specific debounced subject
      this.answerUpdateSubjects.get(questionId).next({
        response,
        personaQuestionsAndAnswers,
        personaUserAnswers
      });
    }
  }

  private buildTitleAndDescription () {
    //  Unanswered Questions - When a user signs in or signs up (uses modal)
    if (this.unansweredQuestionsOnly) {
      //  Signing up
      if (this.newUserForUnansweredQuestions) {
        this.descriptionForComponentInModalUse = this.i18nService.translate(
          'account:newUserForUnansweredQuestionsPersonaBBVN',
          {
            bbvn: 'Blackbaud Verified Network'
          },
          'Share how you\'ll use the __bbvn__ to help us provide relevant information and insights to your nonprofit. Your answers won\'t affect the actions you can take in the __bbvn__ and will not be visible to others. You can update them at any time in My Account and My Nonprofits after claiming your nonprofit.'
        );
      }
      //  Signing in
      else {
        this.descriptionForComponentInModalUse = this.i18nService.translate(
          'account:notNewUserForUnansweredQuestionsPersonaBBVN',
          {
            bbvn: 'Blackbaud Verified Network'
          },
          'We\'ve added a new profile feature that lets you share how you use the __bbvn__. Your answers help us provide relevant insights to your nonprofit. They won\'t affect the actions you can take or be visible to others. These answers apply to any nonprofits you administer. If they differ for specific nonprofits, you can update them in My Nonprofits later.'
        );
      }

      this.title = null;
      this.description = null;
    }//  MyAccounts - Area with other account related forms (no modal used)
    else if (!this.nonprofitId && !this.stagedNonprofitId) {
      this.title = this.i18nService.translate(
        'account:myProfile',
        {},
        'My Profile'
      );

      this.description = this.i18nService.translate(
        'account:myProfilePersonaDescriptionBBVN',
        {
          bbvn: 'Blackbaud Verified Network'
        },
        'Share how you use the __bbvn__ to help us provide relevant information and insights for your nonprofits. Your responses do not impact your access to the __bbvn__ and are not shared with others. You can update your responsibilities for specific nonprofits in My Nonprofits.'
      );

      this.descriptionForComponentInModalUse = null;
    }
    // MyNonprofits - Update your profile nonprofit action (uses modal)
    else if (this.nonprofitId && !this.adminRequest) {
      this.descriptionForComponentInModalUse = this.i18nService.translate(
        'account:myNonprofitsPersonaDescriptionBBVN',
        {
          nonprofitName: this.nonprofitName,
          bbvn: 'Blackbaud Verified Network'
        },
        'Share how you\'re using the __bbvn__ for __nonprofitName__ to help us provide relevant information and insights. Your answers won\'t affect your role for this nonprofit or your access to the __bbvn__. Responses are not visible to others.'
      );

      this.title = null;
      this.description = null;
    }
    // Nonprofit Admin Requests - Used within NAR accordian (no modal used)
    else if ((this.nonprofitId || this.stagedNonprofitId) && this.adminRequest) {
      this.title = null;
      this.description = null;
      this.descriptionForComponentInModalUse = null;
    }
  }

  //  builds the questions and answers of the form
  private buildPersonaQuestionsAndAnswers () {
    this.personaQuestionsAndAnswersData?.map((data) => {
      const formFieldName = data.formFieldName;
      const userAnswersToQuestion = data.personaUserAnswers;
      const questionAndAnswers = data.personaQuestionsAndAnswers;

      //  if the question allows mutliple responses
      if (questionAndAnswers.allowsMultipleResponses) {
        this.buildPersonaQuestionsAndAnswers_MultiAnswerResponse(formFieldName, questionAndAnswers, userAnswersToQuestion)
      } else {  //  if the question is a single response
        this.buildPersonaQuestionsAndAnswers_SingleAnswerResponse(formFieldName, questionAndAnswers, userAnswersToQuestion)
      }
    });
  }

  //  build single response question and answers
  private buildPersonaQuestionsAndAnswers_SingleAnswerResponse (
    formFieldName: string,
    questionAndAnswers: PersonaTypings.PersonaQuestionAndAnswers,
    userAnswersToQuestion: PersonaTypings.PersonaUserAnswers
        | PersonaTypings.PersonaUserNonprofitAnswers
        | PersonaTypings.PersonaUserStagedNonprofitAnswers
        | PersonaTypings.PersonaUserNonprofitAnswersForReview
        | PersonaTypings.PersonaUserStagedNonprofitAnswersForReview
  ) {
    if (this.unansweredQuestionsOnly) {
      if (!(userAnswersToQuestion?.systemAnswers?.length > 0)) {
        this.buildPersonaQuestionAndAnswers_addControl(formFieldName, '', true);
      }
    } else {
      if (userAnswersToQuestion?.systemAnswers?.length > 0) {
        const usersAnswerToTheQuestion = userAnswersToQuestion.systemAnswers[0].systemAnswerId;

        this.buildPersonaQuestionAndAnswers_addControl(formFieldName, usersAnswerToTheQuestion);
      } else {
        this.buildPersonaQuestionAndAnswers_addControl(formFieldName, '');
      }
    }
  }

  //  build multi response question and answers
  private buildPersonaQuestionsAndAnswers_MultiAnswerResponse (
    formFieldName: string,
    questionAndAnswers: PersonaTypings.PersonaQuestionAndAnswers,
    userAnswersToQuestion: PersonaTypings.PersonaUserAnswers
        | PersonaTypings.PersonaUserNonprofitAnswers
        | PersonaTypings.PersonaUserStagedNonprofitAnswers
        | PersonaTypings.PersonaUserNonprofitAnswersForReview
        | PersonaTypings.PersonaUserStagedNonprofitAnswersForReview
  ) {
    if (this.unansweredQuestionsOnly) {
      if (!(userAnswersToQuestion?.systemAnswers?.filter(x => !Number.isNaN(x.systemAnswerId))?.length > 0)) {
        this.buildPersonaQuestionAndAnswers_addControl(formFieldName, [], true);
      }
    } else {
      let formControlValue = null;

      if (userAnswersToQuestion?.systemAnswers?.filter(x => !Number.isNaN(x.systemAnswerId) && x.systemAnswerId !== undefined)?.length > 0) {
        formControlValue = userAnswersToQuestion.systemAnswers
                                .filter(x => !Number.isNaN(x.systemAnswerId)
                                              && x.systemAnswerId !== undefined)
                                .map(x => x.systemAnswerId);
      }

      if (formControlValue && formControlValue.length > 0) {
        this.buildPersonaQuestionAndAnswers_addControl(formFieldName, formControlValue);
      } else {
        this.buildPersonaQuestionAndAnswers_addControl(formFieldName, []);
      }
    }
  }

  //  adds the control to the form and specifies if required or not or will update value if existing
  private buildPersonaQuestionAndAnswers_addControl (
    formFieldName: string,
    usersAnswerToTheQuestion: any,
    requireAnswer: boolean = false
  ) {
    //  if control exists
    if (this.personaForm.contains(formFieldName)) {
      if (typeof usersAnswerToTheQuestion === 'number') {
        if (usersAnswerToTheQuestion !== this.personaForm.get(formFieldName).value) {
          this.personaForm.get(formFieldName).setValue(usersAnswerToTheQuestion);
        }
      } else if (Array.isArray(usersAnswerToTheQuestion)) {
        const answersDiffer = this.personaService.haveMultiResponseQuestionAnswersChanged(this.personaForm.get(formFieldName).value, usersAnswerToTheQuestion);
        if (answersDiffer) {
          this.personaForm.get(formFieldName).setValue(usersAnswerToTheQuestion);
        }
      } else {
        this.personaForm.get(formFieldName).setValue('');
      }
    } else {  //  if control doesn't exist
      if (requireAnswer) {  //  if required
        this.personaForm.addControl(formFieldName, this.formBuilder.control(usersAnswerToTheQuestion, Validators.required));
      } else {  //  if not required
        this.personaForm.addControl(formFieldName, this.formBuilder.control(usersAnswerToTheQuestion));
      }
    }
  }

  //  unsubscribe
  ngOnDestroy () {
    // Complete all question-specific subjects to flush any pending updates
    this.answerUpdateSubjects.forEach(subject => subject.complete());
    this.answerUpdateSubjects.clear();
    this.pendingUpdates.clear();

    this.sub.unsubscribe();

    if (this.adminRequest && this.userId && (this.nonprofitId || this.stagedNonprofitId) && !this.beingReviewed && !this.viewOnly) {
      const personaQuestionAndAnswerTypeWithDetails: PersonaTypings.PersonaQuestionAndAnswerTypeWithDetails = {
        personaQuestionAndAnswerType: this.personaQuestionAndAnswerType,
        nonprofitId: this.nonprofitId,
        stagedNonprofitId: this.stagedNonprofitId,
        userId: this.userId,
        nonprofitAdminRequestId: this.nonprofitAdminRequestId,
        beingReviewed: this.beingReviewed
      };

      this.personaService.addUserSystemAnswersForNonprofitAdminRequest(personaQuestionAndAnswerTypeWithDetails);
    }

    this.personaService.clearComponentData();
  }
}
